/**
 * One dialog for all nine actions — ARCHITECTURE §05.4.
 *
 * The order of what the operator sees is fixed by the spec and implemented
 * literally, top to bottom:
 *
 *   1. what will happen, in one sentence, in the real world;
 *   2. exactly which SIMs — **every MSISDN, scrollable, never elided**;
 *   3. how many, rendered separately, because the count is what people
 *      sanity-check;
 *   4. what is irreversible about it, stated rather than implied;
 *   5. the reason and consent the upstream requires;
 *   6. the type-to-confirm field, last, so it is the final deliberate act.
 *
 * **On the target list.** Criterion 11 fails on "and 27 more", and the reason
 * is worth keeping in front of whoever edits this file: the single most
 * important control before a bulk disconnect is an operator being able to see
 * that the SIM they did *not* mean to select is not in the list. A truncated
 * list removes exactly that, and removes it precisely when the list is long
 * enough for a mistake to hide in.
 *
 * **On focus.** Cancel holds initial focus and Enter inside the confirm field
 * does not submit. A destructive dialog whose confirm button is one Enter away
 * from the previous keystroke is not a confirmation.
 */

import type { SimSummary } from '@rd/shared';
import { useEffect, useId, useMemo, useState, type ReactElement } from 'react';

import { Button } from '../../../components/Button.js';
import { Field } from '../../../components/Field.js';
import { Checkbox, Input, Select } from '../../../components/Input.js';
import { Modal } from '../../../components/Modal.js';
import type { ApiClientError } from '../../../lib/apiClient.js';
import { copyFor } from '../../../lib/errorCopy.js';

import type { SimActionDef } from './actionRegistry.js';

export interface ActionDialogProps {
  action: SimActionDef;
  open: boolean;
  /** Every SIM the action will be applied to. Rendered in full. */
  targets: SimSummary[];
  /** Selected SIMs the action does NOT apply to; disclosed, not hidden. */
  excluded?: SimSummary[];
  /** Rendered between the consequence and the confirm field (plan picker, …). */
  children?: ReactElement | null;
  reason?: string;
  onReasonChange?(next: string): void;
  reasonOptions?: readonly string[];
  consent?: boolean;
  onConsentChange?(next: boolean): void;
  busy?: boolean;
  error?: ApiClientError | null;
  /** Disables Confirm for reasons the dialog cannot see (no plan chosen, …). */
  submitDisabled?: boolean;
  onConfirm(): void;
  onCancel(): void;
}

export function ActionDialog(props: ActionDialogProps): ReactElement | null {
  const {
    action,
    open,
    targets,
    excluded = [],
    children,
    reason,
    onReasonChange,
    reasonOptions,
    consent,
    onConsentChange,
    busy = false,
    error = null,
    submitDisabled = false,
    onConfirm,
    onCancel,
  } = props;

  const [typed, setTyped] = useState('');
  const listId = useId();

  // Every re-open is a fresh deliberate act; a stale confirmation must not
  // carry over.
  useEffect(() => {
    if (open) setTyped('');
  }, [open, action.key]);

  /**
   * Cancel holds initial focus (§05.4).
   *
   * `Modal` focuses the first focusable element in DOM order, which here is
   * whatever the body renders — a reason select, or the type-to-confirm field.
   * For a destructive dialog that is the wrong default: the safe action must be
   * the one already under the user's hands, so a reflexive Enter or Space does
   * nothing. Focused after `Modal`'s own effect, deliberately overriding it.
   */
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      document.querySelector<HTMLButtonElement>('[data-testid="action-cancel"]')?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, action.key]);

  const confirmed = !action.destructive || typed === action.confirmWord;
  const reasonSatisfied = !action.requiresReason || Boolean(reason);
  const canSubmit = confirmed && reasonSatisfied && !submitDisabled && targets.length > 0;

  const copy = error ? copyFor(error) : null;

  const title = useMemo(
    () => `${action.label} — ${targets.length} ${targets.length === 1 ? 'SIM' : 'SIMs'}`,
    [action.label, targets.length],
  );

  if (!open) return null;

  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      // A stray click must not dismiss a confirmation the user is midway
      // through, and must certainly not be mistaken for cancelling safely.
      dismissOnBackdrop={false}
      footer={
        <>
          {/* Cancel holds initial focus: the default action in a destructive
              dialog is to do nothing. `Modal` also focuses the first focusable
              element, and Cancel is deliberately first in the footer. */}
          <Button variant="secondary" onClick={onCancel} data-testid="action-cancel">
            Cancel
          </Button>
          <Button
            variant={action.destructive ? 'danger' : 'primary'}
            loading={busy}
            disabled={!canSubmit}
            onClick={onConfirm}
          >
            {action.label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* 1. What will happen, in the real world. */}
        <p className="text-sm font-medium text-body">{action.consequence}</p>

        {error ? (
          <div role="alert" className="text-sm text-danger">
            <p className="font-medium">{copy?.title}</p>
            <p>{copy?.body}</p>
          </div>
        ) : null}

        {/* 3. How many — separate from the list, because it is what people check. */}
        <p className="text-sm text-muted">
          This applies to{' '}
          <strong className="text-body" data-testid="target-count">
            {targets.length} {targets.length === 1 ? 'SIM' : 'SIMs'}
          </strong>
          .
        </p>

        {/* 2. Exactly which SIMs. Every one. Scrollable, never elided. */}
        <div>
          <p id={listId} className="mb-1 text-xs font-medium text-muted">
            {action.key === 'swap' ? 'SIM numbers' : 'Mobile numbers'}
          </p>
          <ul
            aria-labelledby={listId}
            data-testid="target-list"
            className="max-h-48 overflow-y-auto rounded-[var(--brand-radius)] border border-line bg-surface-alt p-2 font-mono text-xs text-body"
          >
            {targets.map((sim) => (
              <li key={sim.simRef} className="py-0.5">
                {sim.msisdn ?? sim.iccid ?? sim.simRef}
                {action.key === 'swap' && sim.iccid ? ` · ${sim.iccid}` : ''}
              </li>
            ))}
          </ul>
        </div>

        {/* Mixed eligibility is disclosed, never quietly dropped. */}
        {excluded.length > 0 ? (
          <div data-testid="excluded-list" className="text-xs text-muted">
            <p className="font-medium">
              {excluded.length} selected {excluded.length === 1 ? 'SIM is' : 'SIMs are'} not
              eligible and will not be included:
            </p>
            <ul className="mt-1 max-h-24 overflow-y-auto font-mono">
              {excluded.map((sim) => (
                <li key={sim.simRef}>
                  {sim.msisdn ?? sim.simRef} — {sim.status ?? 'unknown status'}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {children}

        {/* 5. Reason and consent, where the upstream requires them. */}
        {action.requiresReason && reasonOptions ? (
          <Field label="Reason" required>
            {(fieldProps) => (
              <Select
                {...fieldProps}
                value={reason ?? ''}
                onChange={(e) => onReasonChange?.(e.target.value)}
              >
                <option value="">Choose a reason…</option>
                {reasonOptions.map((option) => (
                  <option key={option} value={option}>
                    {humanise(option)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {action.offersConsent && onConsentChange ? (
          <label className="flex items-start gap-2 text-sm text-body">
            <Checkbox
              checked={consent === true}
              onChange={(e) => onConsentChange(e.target.checked)}
            />
            <span>I confirm the customer has agreed to this change.</span>
          </label>
        ) : null}

        {/* 6. Type-to-confirm, last. */}
        {action.destructive ? (
          <Field
            label={`Type ${action.confirmWord} to confirm`}
            hint="This cannot be undone from this panel."
            required
          >
            {(fieldProps) => (
              <Input
                {...fieldProps}
                value={typed}
                autoComplete="off"
                data-testid="confirm-input"
                onChange={(e) => setTyped(e.target.value)}
                // Enter must not submit: the confirm button is a separate,
                // deliberate act, not the tail of a keystroke.
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            )}
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}

/** `NUMBER_NOT_REQUIRED` → `Number not required`. */
export function humanise(value: string): string {
  const lower = value.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
